import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const HERO_IMG = "https://images.unsplash.com/photo-1678028204645-df95d07e2523?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const Line = ({ children, delay }) => (
    <span className="hero-mask-line">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
            <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
                <img
                    src={HERO_IMG}
                    alt="Goldenes Abendlicht über einem ruhigen Grasfeld"
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#161412]/35 via-[#161412]/25 to-[#161412]/60" />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 w-full pb-20 sm:pb-28 pt-40">
                <h1
                    data-testid="hero-title"
                    className="font-serif font-light text-[#FAF8F5] text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.08] tracking-tight max-w-4xl"
                >
                    <Line delay={0.4}>Psychosoziale Beratung{" "}</Line>
                    <Line delay={0.55}>
                        <span className="italic">für Jugendliche & junge Erwachsene</span>
                    </Line>
                </h1>

                <motion.p
                    data-testid="hero-subtitle"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.95 }}
                    className="mt-8 max-w-2xl font-sans text-base sm:text-lg font-light leading-relaxed text-[#FAF8F5]/85"
                >
                    Manchmal wird das Leben einfach zu viel. Vielleicht steckst du gerade in einer Krise, lebst mit
                    einer chronischen Erkrankung oder fühlst dich durch Schule, Ausbildung, Familie, Beziehungen oder
                    Zukunftsfragen belastet. Gemeinsam schauen wir auf deine Situation, sortieren Gedanken und
                    Gefühle und suchen nach Möglichkeiten, die für dich hilfreich und machbar sind.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.15 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-btn-kennenlernen"
                        onClick={() => scrollToSection("#kontakt")}
                        className="bg-[#FAF8F5] text-[#2B2825] font-sans text-[12px] font-medium tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#E6E1D5] transition-colors duration-300"
                    >
                        Unverbindlich kennenlernen
                    </button>
                </motion.div>
            </div>

            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                onClick={() => scrollToSection("#ueber-mich")}
                data-testid="hero-scroll-hint"
                className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-2 text-[#FAF8F5]/70 hover:text-[#FAF8F5] transition-colors"
            >
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Scroll</span>
                <ArrowDown size={15} className="animate-bounce" />
            </motion.button>
        </section>
    );
};
